import MultiSelectDropdown from "../dropdowns/multiSelectDropdown";
import {useQuery} from "@tanstack/react-query";
import {DROPDOWN_PRELOAD_SIZE, ROV_NAME, VESSEL_NAME} from "../../utils/constants";
import useApiClient from "../../hooks/useApiClient";
import { DataAccessService, RealAssetDeploymentResponse } from "../../apiClient";
import {getRealAssetRecordForExactNameMatch, QUERY_OPTIONS_LOAD_ONCE, renderDeploymentName} from "../../apiUtils";
import {useState} from "react";
import {useDebouncedFunction} from "../../hooks/useDebouncedQueryParams";
import {DEBOUNCE_DELAY} from "../../utils";
import {MultiSelectDropdownSmartWrapperProps} from "../dropdowns/types";


type ListRealAssetDeploymentRequestOptions = Parameters<DataAccessService["listRealAssetDeployments"]>[0]

interface RealAssetDeploymentSmartDropdownProps<OptionType, SelectionType> {
    apiOptions?: ListRealAssetDeploymentRequestOptions,
    dropdownProps: MultiSelectDropdownSmartWrapperProps<OptionType, SelectionType>
}


export const RealAssetDeploymentDropdown = ({
    apiOptions,
    dropdownProps,
}: RealAssetDeploymentSmartDropdownProps<RealAssetDeploymentResponse, string>) => {
    const api = useApiClient();
    const [debouncedSearchTerm, _setDebounceSearchTerm] = useState("");
    const setDebounceSearchTerm = useDebouncedFunction(
        _setDebounceSearchTerm,
      DEBOUNCE_DELAY
    );

    const mergedApiOptions = {
        size: DROPDOWN_PRELOAD_SIZE,
        query: debouncedSearchTerm || undefined,
        ...apiOptions
    }

    const { data: deployments, isLoading: deploymentsAreLoading } = useQuery({
    queryKey: ["real-asset-deployment-list", Object.entries(mergedApiOptions)],
    queryFn: async () => {
      const deployments = await api.dataAccess.listRealAssetDeployments({
          ...mergedApiOptions
      });
      return deployments.items;
    },
    ...QUERY_OPTIONS_LOAD_ONCE,
    enabled: !dropdownProps.isLoading,
  });

    return (
        <MultiSelectDropdown<RealAssetDeploymentResponse, string>
            label={"Real Asset Deployment"}
            options={deployments ?? []}
            selections={[]}
            isLoading={dropdownProps.isLoading || deploymentsAreLoading}
            renderOption={renderDeploymentName}
            onSearchTermChange={setDebounceSearchTerm}
            mapOptionToSelection={o => o.public_id ?? ""}
            {...dropdownProps}
        />
    )
}


export const CruiseDropdown  = (dropdownProps: MultiSelectDropdownSmartWrapperProps<RealAssetDeploymentResponse, string>) => {
    const api = useApiClient();
    const { data: vessel, isLoading: cruisesAreLoading } = useQuery({
    queryKey: ["oxr-real-asset"],
    queryFn: async () => getRealAssetRecordForExactNameMatch(
            api,
            VESSEL_NAME
        ),
    })
    return (
        <RealAssetDeploymentDropdown
            dropdownProps={{
                label: "Cruises",
                isLoading: cruisesAreLoading,
                ...dropdownProps
            }}
            apiOptions={{realAssetPublicId: vessel?.public_id}}
        />
    )
}

interface RovLoweringDropdownProps extends MultiSelectDropdownSmartWrapperProps<RealAssetDeploymentResponse, string>{
    cruises: string[]
}

export const RovLoweringDropdown = ({cruises, ...dropdownOptions}: RovLoweringDropdownProps) => {
    const api = useApiClient();

      const { data: rovRealAsset, isLoading: rovRealAssetIsLoading } = useQuery({
        queryKey: ["rov-real-asset"],
        queryFn: () => getRealAssetRecordForExactNameMatch(api, ROV_NAME),
        throwOnError: true,
        refetchOnWindowFocus: false,
      });

        return (
        <RealAssetDeploymentDropdown
            dropdownProps={{
                label: "ROV Lowerings",
                isLoading: rovRealAssetIsLoading,
                ...dropdownOptions
            }}
            apiOptions={{parentDeploymentExternalId: cruises || null,
              realAssetPublicId: [rovRealAsset?.public_id]}}
        />
    )
}